<template>
  <div class="modern-header d-flex justify-space-between">
    <div class="d-flex flex-column">
      <h1 class="text-h5 font-weight-bold ma-0">
        <slot name="header-title" />
      </h1>
      <span class="text-caption">
        <slot name="header-caption" />
      </span>
    </div>
    <div class="d-flex align-center">
      <!-- Header Actions slot (for ViewToggle, buttons, etc.) -->
      <slot name="header-actions" />

      <!-- Product Switcher -->
      <product-switcher
        v-if="showProductSwitcher && products.length > 0"
        :products="products"
        @product-click="handleProductClick"
      />

      <!-- User menu -->
      <v-menu offset-y>
        <template #activator="{ on, attrs }">
          <v-btn
            class="user-menu-btn text-body-2 blue-text darken-2 text-capitalize"
            text
            v-bind="attrs"
            v-on="on"
          >
            <span class="user-initials">
              {{ userInitials }}
            </span>
            <span>Hello {{ userName }}</span>
            <v-icon>
              mdi-chevron-down
            </v-icon>
          </v-btn>
        </template>
        <v-list class="user-menu-list">
          <v-list-item @click="handleSupport">
            <lucide-icon name="HelpCircle" :size="18" class="mr-3" />
            Support
          </v-list-item>
          <v-list-item @click="handleLearningHub">
            <lucide-icon name="GraduationCap" :size="18" class="mr-3" />
            Learning HUB
          </v-list-item>
          <v-divider class="my-2" />
          <v-list-item @click="handleLogout">
            <lucide-icon name="LogOut" :size="18" class="mr-3" />
            Log Out
          </v-list-item>
        </v-list>
      </v-menu>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from 'vue';
import ProductSwitcher from '../ProductSwitcher';
import type { Product } from '../ProductSwitcher';
import LucideIcon from '../LucideIcon';

export default Vue.extend({
  name: 'ModernHeader',

  components: {
    ProductSwitcher,
    LucideIcon
  },

  props: {
    userName: {
      type: String,
      default: 'User'
    },
    // Product switcher
    showProductSwitcher: {
      type: Boolean,
      default: true
    },
    products: {
      type: Array as () => Product[],
      default: () => []
    }
  },

  computed: {
    userInitials(): string {
      return this.userName
        .split(' ')
        .map(n => n[0])
        .join('')
        .toUpperCase()
        .substring(0, 2);
    }
  },

  methods: {
    handleLogout() {
      this.$emit('logout');
    },

    handleSupport() {
      this.$emit('support');
    },

    handleLearningHub() {
      this.$emit('learning-hub');
    },

    handleProductClick(product: Product) {
      this.$emit('product-click', product);
    }
  }
});
</script>

<style lang="scss" scoped>
@import './styles.scss';

$inter-font-stack: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', Arial, sans-serif;
$mont-font-stack: 'Mont', sans-serif;

// Modern header - uses Mont for headings, Inter for UI elements
.modern-header {
  font-family: $inter-font-stack !important;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: optimizeLegibility;

  // Override Vuetify typography classes - use Inter for UI elements
  .text-h6,
  .text-subtitle-1,
  .text-subtitle-2,
  .text-body-1,
  .text-body-2 {
    font-family: $inter-font-stack !important;
  }

  // Page title - use Mont font to align with brand
  h1,
  .text-h5 {
    font-family: $mont-font-stack !important;
    font-size: 1.5rem !important; // 24px - aligned with NextJS text-2xl
    font-weight: 700 !important;
    line-height: 2rem !important;
    letter-spacing: normal !important;
    color: var(--global-header-text-color) !important;
    margin-bottom: 0 !important;
  }

  // Caption styling - clear hierarchy, uses Inter
  .text-caption {
    font-family: $inter-font-stack !important;
    font-size: 0.875rem !important; // 14px - aligned with NextJS text-sm
    font-weight: 400 !important;
    line-height: 1.25rem !important;
    letter-spacing: normal !important;
    color: var(--global-muted-foreground) !important;
    margin-top: 0.25rem !important;
  }
}

// User menu button styling - consistent with AppHeader
.user-menu-btn {
  font-family: $inter-font-stack !important;
  font-weight: 500 !important;
  text-transform: none !important;
  letter-spacing: -0.006em !important;
  font-size: 14px !important;
  padding: 6px 12px !important;
  border-radius: var(--radius) !important;
  transition: all 0.15s ease !important;

  &:hover {
    background-color: var(--muted) !important;
  }

  // Text spans (excluding user-initials)
  span:not(.user-initials) {
    font-family: $inter-font-stack !important;
    color: var(--muted-foreground) !important;
  }

  .v-icon {
    color: var(--muted-foreground) !important;
  }

  // User initials roundel - circular avatar with initials
  .user-initials {
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    width: 32px !important;
    height: 32px !important;
    min-width: 32px !important;
    min-height: 32px !important;
    border-radius: 50% !important;
    background-color: var(--brand-steel-blue) !important;
    color: #ffffff !important;
    font-size: 12px !important;
    font-weight: 600 !important;
    line-height: 1 !important;
    text-align: center !important;
    flex-shrink: 0 !important;
    margin-right: 8px !important;
    padding: 0 !important;
    letter-spacing: 0 !important;
    text-indent: 0 !important;
  }
}

// User menu list styling (using ::v-deep since v-menu renders in portal)
::v-deep .user-menu-list {
  font-family: $inter-font-stack !important;
  padding: 8px 0 !important;
  background-color: var(--card) !important;
  border: 1px solid var(--border) !important;
  border-radius: var(--radius) !important;
  box-shadow: var(--shadow-lg) !important;
  min-width: 220px !important;

  .v-list-item {
    font-family: $inter-font-stack !important;
    font-size: 14px !important;
    font-weight: 500 !important;
    color: var(--foreground) !important;
    padding: 10px 16px !important;
    min-height: 40px !important;

    &:hover {
      background-color: var(--muted) !important;
    }
  }

  .v-divider {
    border-color: var(--border) !important;
  }
}
</style>
